const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';
// WebSocket 地址：优先环境变量；否则按当前页面协议/域名自动推导（经 Caddy /ws 反代到 T-Engine 8765）。
// 不能用 localhost——那指向访客自己的电脑，导致线上永远连不上（2026-09-15 修复）。
const _defaultWsUrl = (() => {
  if (typeof window === 'undefined') return 'ws://localhost:8765';
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return proto + "//" + window.location.host + "/ws";
})();
export const WS_URL = import.meta.env.VITE_WS_URL || _defaultWsUrl;

interface TokenPair {
  accessToken: string;
  refreshToken: string;
  user: { id: number; email: string };
}

let accessToken = localStorage.getItem('access_token') || '';
let refreshToken = localStorage.getItem('refresh_token') || '';
let refreshPromise: Promise<string | null> | null = null;

export function setTokens(tokens: TokenPair) {
  accessToken = tokens.accessToken;
  refreshToken = tokens.refreshToken;
  localStorage.setItem('access_token', accessToken);
  localStorage.setItem('refresh_token', refreshToken);
  localStorage.setItem('user_email', tokens.user.email);
}

export function clearTokens() {
  accessToken = '';
  refreshToken = '';
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('user_email');
}

export function getAccessToken() {
  return accessToken;
}

export function createWebSocket(path = '') {
  const token = accessToken;
  const base = `${WS_URL}${path}`;
  const url = token ? `${base}${base.includes('?') ? '&' : '?'}token=${encodeURIComponent(token)}` : base;
  return new WebSocket(url);
}

export function getRefreshToken() {
  return refreshToken;
}

export function getStoredUserEmail() {
  return localStorage.getItem('user_email') || '';
}

export function isLoggedIn() {
  return !!accessToken;
}

async function doRefresh(): Promise<string | null> {
  if (!refreshToken) return null;
  try {
    const res = await fetch(`${API_BASE_URL}/api/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });
    if (!res.ok) throw new Error('refresh failed');
    const data = await res.json();
    accessToken = data.accessToken;
    localStorage.setItem('access_token', accessToken);
    if (data.user?.email) {
      localStorage.setItem('user_email', data.user.email);
    }
    return accessToken;
  } catch (err) {
    clearTokens();
    window.location.href = '/login';
    return null;
  }
}

async function refreshAccessToken(): Promise<string | null> {
  if (!refreshPromise) {
    refreshPromise = doRefresh().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

export async function apiFetch(path: string, options: RequestInit = {}) {
  const url = `${API_BASE_URL}${path}`;
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string> || {}),
  };
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  let res = await fetch(url, { ...options, headers });

  if (res.status === 401 && refreshToken) {
    const newToken = await refreshAccessToken();
    if (newToken) {
      headers['Authorization'] = `Bearer ${newToken}`;
      res = await fetch(url, { ...options, headers });
    }
  }

  return res;
}

export async function login(email: string, password: string) {
  const res = await apiFetch('/api/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || '登录失败');
  setTokens(data);
  return data.user;
}

export async function register(email: string, password: string) {
  const res = await apiFetch('/api/auth/register', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || '注册失败');
  setTokens(data);
  return data.user;
}

export async function phoneRegister(phone: string, password: string) {
  const res = await apiFetch('/api/auth/phone/register', {
    method: 'POST',
    body: JSON.stringify({ phone, password }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || '注册失败');
  setTokens(data);
  return data.user;
}

export async function phoneLogin(phone: string, password: string) {
  const res = await apiFetch('/api/auth/phone/login', {
    method: 'POST',
    body: JSON.stringify({ phone, password }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || '登录失败');
  setTokens(data);
  return data.user;
}

export async function bindPhone(phone: string) {
  const res = await apiFetch('/api/auth/phone/bind', {
    method: 'POST',
    body: JSON.stringify({ phone }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || '绑定失败');
  return data.user;
}

export async function bindWechatCode(code: string) {
  const res = await apiFetch('/api/auth/wechat/bind', {
    method: 'POST',
    body: JSON.stringify({ code }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || '绑定失败');
  return data.user;
}

export async function fetchMe() {
  const res = await apiFetch('/api/auth/me');
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || '获取用户信息失败');
  return data.user;
}

export async function logout() {
  await apiFetch('/api/auth/logout', {
    method: 'POST',
    body: JSON.stringify({ refreshToken }),
  }).catch(() => {});
  clearTokens();
}

interface StreamCallbacks {
  onChunk?: (chunk: { content: string; type?: string }) => void;
  onDone?: () => void;
  onError?: (error: string) => void;
}

export function apiFetchStream(path: string, options: RequestInit = {}, callbacks: StreamCallbacks) {
  const url = `${API_BASE_URL}${path}`;
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'Accept': 'text/event-stream',
    ...(options.headers as Record<string, string> || {}),
  };
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  const controller = new AbortController();
  let closed = false;

  const start = async () => {
    try {
      let res = await fetch(url, { ...options, headers, signal: controller.signal });

      if (res.status === 401 && refreshToken) {
        const newToken = await refreshAccessToken();
        if (newToken) {
          headers['Authorization'] = `Bearer ${newToken}`;
          res = await fetch(url, { ...options, headers, signal: controller.signal });
        }
      }

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `HTTP ${res.status}`);
      }

      const reader = res.body?.getReader();
      if (!reader) throw new Error('No response body');
      const decoder = new TextDecoder('utf-8');
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        let currentEvent = '';
        let currentData = '';
        for (const line of lines) {
          if (line.startsWith('event:')) {
            currentEvent = line.slice(6).trim();
          } else if (line.startsWith('data:')) {
            currentData = line.slice(5).trim();
          } else if (line.trim() === '' && currentEvent && currentData) {
            try {
              const payload = JSON.parse(currentData);
              if (currentEvent === 'chunk') callbacks.onChunk?.(payload);
              else if (currentEvent === 'done') callbacks.onDone?.();
              else if (currentEvent === 'error') callbacks.onError?.(payload.error || '流式输出错误');
            } catch (e) {
              // ignore malformed events
            }
            currentEvent = '';
            currentData = '';
          }
        }
      }

      if (!closed) callbacks.onDone?.();
    } catch (err: any) {
      if (err.name !== 'AbortError') {
        callbacks.onError?.(err.message || '网络错误');
      }
    }
  };

  start();

  return {
    abort: () => {
      closed = true;
      controller.abort();
    },
  };
}
